import { useReloadTranslations, useTranslationsGetUiBundle } from '@/hooks/useTranslations'
import { TranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { languageOptions, type LanguageOption } from 'BrokerTsLib/src/enums'
import React, { useCallback, useMemo } from 'react'
import { useAuth } from './AuthContext'

interface TranslationsProviderProps {
  children?: React.ReactNode
}

export const TranslationsProvider = ({ children }: TranslationsProviderProps) => {
  const { mutateAsync: reloadTranslations } = useReloadTranslations()
  const { data: translations, refetch: refetchUiBundle } = useTranslationsGetUiBundle()
  const { user } = useAuth()

  const translate = useCallback(
    (key: string, defaultValue?: string) => {
      if (!translations)
        return defaultValue ?? key

      return translations[key] ?? defaultValue ?? key
    },
    [translations]
  )

  const getLanguage = useCallback((): LanguageOption => {
    return languageOptions.find(x => x.id === user?.languageId) ?? languageOptions[0]
  }, [user])

  const reload = useCallback(async () => {
    await reloadTranslations()
    refetchUiBundle()
  }, [reloadTranslations, refetchUiBundle])

  const translateFormat = useCallback(
    (
      key: string,
      defaultValue: string,
      params?: Record<string, React.ReactNode>
    ): React.ReactNode => {
      const template = translations?.[key] ?? defaultValue ?? key

      if (!params)
        return template

      const parts: React.ReactNode[] = []

      const regex = /\{(\w+)\}/g
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = regex.exec(template)) !== null) {
        const [placeholder, paramName] = match

        // text před placeholderem
        if (match.index > lastIndex) {
          parts.push(template.slice(lastIndex, match.index))
        }

        // hodnota parametru
        const value = params[paramName]

        if (value !== undefined) {
          parts.push(value)
        } else {
          parts.push(placeholder) // fallback
        }

        lastIndex = match.index + placeholder.length
      }

      // zbytek textu
      if (lastIndex < template.length) {
        parts.push(template.slice(lastIndex))
      }

      return parts
    },
    [translations]
  )

  const value = useMemo(
    () => ({
      translations: translations ?? {},
      translate,
      reload: reload,
      getLanguage,
      translateFormat
    }),
    [translations, translate, reload, getLanguage, translateFormat]
  )

  return (
    <TranslationsContext.Provider value={value}>
      {children && children}
    </TranslationsContext.Provider>
  )
}